// DriftTimeline.js — Vertical CSS timeline for asset scan/drift history
import React, { useState, useMemo } from 'react';
import './DriftTimeline.css';

/**
 * Determine the timeline dot status from an entry.
 * Maps driftStatus / type to one of: alert, changed, stable, info.
 */
function getDotStatus(entry) {
    if (entry.driftStatus === 'alert') return 'alert';
    if (entry.driftStatus === 'changed') return 'changed';
    if (entry.driftStatus === 'stable') return 'stable';
    // Baselines and info-only entries get the neutral dot
    if (entry.type === 'baseline') return 'info';
    // Fallback: if there are critical/high findings, show alert
    if (entry.vulnCritical > 0 || entry.vulnHigh > 0) return 'changed';
    return 'stable';
}

/**
 * Format an ISO date string to a concise display format.
 */
function formatDate(iso) {
    if (!iso) return '\u2014';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Derive the display type for a timeline entry.
 * The oldest entry in the set is labeled BASELINE unless it already has a type.
 */
function getEntryType(entry, isOldest) {
    if (entry.type === 'drift' || entry.type === 'drift_event') return 'drift';
    if (entry.type === 'baseline' || isOldest) return 'baseline';
    return 'scan';
}

/**
 * Build a one-line summary from the entry data.
 */
function buildSummary(entry) {
    const parts = [];
    const totalVulns = entry.vulnTotal || 0;
    if (totalVulns > 0) {
        parts.push(`${totalVulns} finding${totalVulns !== 1 ? 's' : ''}`);
    }
    if (entry.openPortCount != null) {
        parts.push(`${entry.openPortCount} open port${entry.openPortCount !== 1 ? 's' : ''}`);
    }
    if (entry.subdomainCount != null && entry.subdomainCount > 0) {
        parts.push(`${entry.subdomainCount} subdomain${entry.subdomainCount !== 1 ? 's' : ''}`);
    }
    if (entry.driftSummary) {
        return entry.driftSummary;
    }
    return parts.length > 0 ? parts.join(' \u00B7 ') : 'Scan completed';
}

/**
 * Build the risk score delta string (e.g. "+12" or "-5").
 */
function riskDelta(entry, prevEntry) {
    if (entry.riskScore == null || !prevEntry || prevEntry.riskScore == null) return null;
    const diff = entry.riskScore - prevEntry.riskScore;
    if (diff === 0) return null;
    return diff > 0 ? `+${diff}` : `${diff}`;
}

function DriftTimeline({ assetId, history, limit = 20 }) {
    const [expandedIds, setExpandedIds] = useState({});

    // Sort descending by date and slice to limit
    const entries = useMemo(() => {
        if (!history || history.length === 0) return [];
        const sorted = [...history].sort((a, b) => {
            const da = new Date(a.completedAt || 0);
            const db = new Date(b.completedAt || 0);
            return db - da;
        });
        return sorted.slice(0, limit);
    }, [history, limit]);

    // We need the ascending-order list too, to compute deltas
    const entriesAsc = useMemo(() => [...entries].reverse(), [entries]);

    const toggleExpand = (id) => {
        setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
    };

    if (!history || history.length === 0) {
        return (
            <div className="drift-timeline-empty">
                <p>No timeline data yet.</p>
            </div>
        );
    }

    // The oldest entry in the full set (for BASELINE label)
    const oldestId = entriesAsc.length > 0
        ? (entriesAsc[0].resultId || entriesAsc[0].completedAt)
        : null;

    return (
        <div className="drift-timeline">
            {entries.map((entry, idx) => {
                const entryId = entry.resultId || entry.completedAt || idx;
                const isOldest = (entry.resultId || entry.completedAt) === oldestId;
                const entryType = getEntryType(entry, isOldest);
                const dotStatus = getDotStatus(entry);
                const isExpanded = !!expandedIds[entryId];

                // Find previous entry (next in desc array = previous in time)
                const prevEntry = idx < entries.length - 1 ? entries[idx + 1] : null;
                const delta = riskDelta(entry, prevEntry);

                return (
                    <div key={entryId} className="drift-timeline-entry">
                        <div className="drift-timeline-dot" data-status={dotStatus} />

                        <div className="drift-timeline-date">
                            {formatDate(entry.completedAt)}
                        </div>

                        <span className="drift-timeline-type" data-type={entryType}>
                            {entryType === 'drift' ? 'DRIFT EVENT' : entryType === 'baseline' ? 'BASELINE' : 'SCAN'}
                        </span>

                        <div className="drift-timeline-summary">
                            {buildSummary(entry)}
                        </div>

                        {(delta || entry.riskScore != null) && (
                            <div className="drift-timeline-summary-metric">
                                {entry.riskScore != null && (
                                    <span>Risk: {entry.riskScore}</span>
                                )}
                                {delta && (
                                    <span> ({delta})</span>
                                )}
                            </div>
                        )}

                        {/* Expandable detail toggle */}
                        <button
                            className="drift-timeline-toggle"
                            onClick={() => toggleExpand(entryId)}
                            aria-expanded={isExpanded}
                        >
                            <span className={`drift-timeline-chevron ${isExpanded ? 'drift-timeline-chevron--open' : ''}`}>
                                &#9654;
                            </span>
                            {isExpanded ? 'Hide details' : 'Show details'}
                        </button>

                        {/* Expandable detail content */}
                        {isExpanded && (
                            <div className="drift-timeline-detail">
                                <div className="drift-timeline-detail-row">
                                    <span className="drift-timeline-detail-label">Open Ports</span>
                                    <span className="drift-timeline-detail-value">
                                        {entry.openPortCount != null ? entry.openPortCount : '\u2014'}
                                    </span>
                                </div>
                                <div className="drift-timeline-detail-row">
                                    <span className="drift-timeline-detail-label">Subdomains</span>
                                    <span className="drift-timeline-detail-value">
                                        {entry.subdomainCount != null ? entry.subdomainCount : '\u2014'}
                                    </span>
                                </div>
                                <div className="drift-timeline-detail-row">
                                    <span className="drift-timeline-detail-label">Critical</span>
                                    <span className="drift-timeline-detail-value">
                                        {entry.vulnCritical != null ? entry.vulnCritical : '\u2014'}
                                    </span>
                                </div>
                                <div className="drift-timeline-detail-row">
                                    <span className="drift-timeline-detail-label">High</span>
                                    <span className="drift-timeline-detail-value">
                                        {entry.vulnHigh != null ? entry.vulnHigh : '\u2014'}
                                    </span>
                                </div>
                                <div className="drift-timeline-detail-row">
                                    <span className="drift-timeline-detail-label">Medium</span>
                                    <span className="drift-timeline-detail-value">
                                        {entry.vulnMedium != null ? entry.vulnMedium : '\u2014'}
                                    </span>
                                </div>
                                <div className="drift-timeline-detail-row">
                                    <span className="drift-timeline-detail-label">Low</span>
                                    <span className="drift-timeline-detail-value">
                                        {entry.vulnLow != null ? entry.vulnLow : '\u2014'}
                                    </span>
                                </div>
                                <div className="drift-timeline-detail-row">
                                    <span className="drift-timeline-detail-label">Total Findings</span>
                                    <span className="drift-timeline-detail-value">
                                        {entry.vulnTotal != null ? entry.vulnTotal : '\u2014'}
                                    </span>
                                </div>
                                {entry.riskScore != null && (
                                    <div className="drift-timeline-detail-row">
                                        <span className="drift-timeline-detail-label">Risk Score</span>
                                        <span className="drift-timeline-detail-value">{entry.riskScore}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action links */}
                        {entry.resultId && (
                            <div className="drift-timeline-actions">
                                <button
                                    className="drift-timeline-action"
                                    onClick={() => {
                                        const diffSection = document.getElementById('drift-diff-section');
                                        if (diffSection) diffSection.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                >
                                    View Diff
                                </button>
                                <a
                                    className="drift-timeline-action"
                                    href={`/assets/${assetId}?tab=results&result=${entry.resultId}`}
                                >
                                    View Full Scan
                                </a>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

export default DriftTimeline;
